/**
 * Data Reducer
 * Manages application data state (dashboard, wallet, trades, etc.)
 */

const initialState = {
  dashboard: null,
  wallet: {
    balance: null,
    transactions: [],
  },
  trades: {
    all: [],
    active: [],
    history: [],
    stats: null,
    forex: {
      data: [],
      pagination: null,
      stats: null,
    },
    crypto: {
      data: [],
      pagination: null,
      stats: null,
    },
    gold: {
      data: [],
      pagination: null,
      stats: null,
    },
  },
  referrals: null,
  kyc: null,
  kycDetails: null,
  paymentMethods: [],
  bankAccounts: [],
  transactions: [],
  symbols: [],
  popups: [],
  loading: {
    dashboard: false,
    wallet: false,
    trades: false,
    referrals: false,
    kyc: false,
    bankAccounts: false,
    forexTrades: false,
    cryptoTrades: false,
    goldTrades: false,
  },
  error: null,
};

const dataReducer = (state = initialState, action) => {
  switch (action.type) {
    // Dashboard
    case 'FETCH_DASHBOARD_START':
      return {
        ...state,
        loading: { ...state.loading, dashboard: true },
        error: null,
      };
    case 'FETCH_DASHBOARD_SUCCESS':
      return {
        ...state,
        dashboard: action.payload,
        loading: { ...state.loading, dashboard: false },
      };
    case 'FETCH_DASHBOARD_FAILURE':
      return {
        ...state,
        loading: { ...state.loading, dashboard: false },
        error: action.payload,
      };

    // Wallet
    case 'FETCH_WALLET_BALANCE_SUCCESS':
      return {
        ...state,
        wallet: {
          ...state.wallet,
          balance: action.payload,
        },
      };
    case 'FETCH_WALLET_TRANSACTIONS_SUCCESS':
      return {
        ...state,
        wallet: {
          ...state.wallet,
          transactions: action.payload,
        },
      };

    // Trades
    case 'FETCH_TRADES_START':
      return {
        ...state,
        loading: { ...state.loading, trades: true },
      };
    case 'FETCH_ALL_TRADES_SUCCESS':
      return {
        ...state,
        trades: {
          ...state.trades,
          all: action.payload,
        },
        loading: { ...state.loading, trades: false },
      };
    case 'FETCH_ACTIVE_TRADES_SUCCESS':
      return {
        ...state,
        trades: {
          ...state.trades,
          active: action.payload,
        },
      };
    case 'FETCH_TRADE_HISTORY_SUCCESS':
      return {
        ...state,
        trades: {
          ...state.trades,
          history: action.payload,
        },
      };
    case 'FETCH_TRADE_STATS_SUCCESS':
      return {
        ...state,
        trades: {
          ...state.trades,
          stats: action.payload,
        },
      };

    // Forex Trades
    case 'FETCH_FOREX_TRADES_START':
      return {
        ...state,
        loading: { ...state.loading, forexTrades: true },
        error: null,
      };
    case 'FETCH_FOREX_TRADES_SUCCESS':
      return {
        ...state,
        trades: {
          ...state.trades,
          forex: {
            data: action.payload?.trades || action.payload?.data || [],
            pagination: action.payload?.pagination || null,
            stats: action.payload?.stats || null,
          },
        },
        loading: { ...state.loading, forexTrades: false },
      };
    case 'FETCH_FOREX_TRADES_FAILURE':
      return {
        ...state,
        loading: { ...state.loading, forexTrades: false },
        error: action.payload,
      };

    // Crypto Trades
    case 'FETCH_CRYPTO_TRADES_START':
      return {
        ...state,
        loading: { ...state.loading, cryptoTrades: true },
        error: null,
      };
    case 'FETCH_CRYPTO_TRADES_SUCCESS':
      return {
        ...state,
        trades: {
          ...state.trades,
          crypto: {
            data: action.payload?.trades || action.payload?.data || [],
            pagination: action.payload?.pagination || null,
            stats: action.payload?.stats || null,
          },
        },
        loading: { ...state.loading, cryptoTrades: false },
      };
    case 'FETCH_CRYPTO_TRADES_FAILURE':
      return {
        ...state,
        loading: { ...state.loading, cryptoTrades: false },
        error: action.payload,
      };

    // Gold Trades
    case 'FETCH_GOLD_TRADES_START':
      return {
        ...state,
        loading: { ...state.loading, goldTrades: true },
        error: null,
      };
    case 'FETCH_GOLD_TRADES_SUCCESS':
      return {
        ...state,
        trades: {
          ...state.trades,
          gold: {
            data: action.payload?.trades || action.payload?.data || [],
            pagination: action.payload?.pagination || null,
            stats: action.payload?.stats || null,
          },
        },
        loading: { ...state.loading, goldTrades: false },
      };
    case 'FETCH_GOLD_TRADES_FAILURE':
      return {
        ...state,
        loading: { ...state.loading, goldTrades: false },
        error: action.payload,
      };

    // Referrals
    case 'FETCH_REFERRALS_SUCCESS':
      return {
        ...state,
        referrals: action.payload,
      };

    // KYC
    case 'FETCH_KYC_START':
      return {
        ...state,
        loading: { ...state.loading, kyc: true },
      };
    case 'FETCH_KYC_SUCCESS':
      return {
        ...state,
        kyc: action.payload, // Can be null if KYC hasn't been submitted
        loading: { ...state.loading, kyc: false },
      };
    case 'FETCH_KYC_FAILURE':
      return {
        ...state,
        loading: { ...state.loading, kyc: false },
        error: action.payload,
      };
    case 'SUBMIT_KYC_START':
    case 'UPDATE_KYC_START':
      return {
        ...state,
        loading: { ...state.loading, kyc: true },
      };
    case 'SUBMIT_KYC_FAILURE':
    case 'UPDATE_KYC_FAILURE':
      return {
        ...state,
        loading: { ...state.loading, kyc: false },
        error: action.payload,
      };
    case 'FETCH_KYC_DETAILS_START':
      return {
        ...state,
        loading: { ...state.loading, kycDetails: true },
      };
    case 'FETCH_KYC_DETAILS_SUCCESS':
      return {
        ...state,
        kycDetails: action.payload,
        loading: { ...state.loading, kycDetails: false },
      };
    case 'FETCH_KYC_DETAILS_FAILURE':
      return {
        ...state,
        loading: { ...state.loading, kycDetails: false },
        error: action.payload,
      };

    // Payment Methods
    case 'FETCH_PAYMENT_METHODS_SUCCESS':
      return {
        ...state,
        paymentMethods: action.payload,
      };

    // Bank Accounts
    case 'FETCH_BANK_ACCOUNTS_START':
      return {
        ...state,
        loading: { ...state.loading, bankAccounts: true },
      };
    case 'FETCH_BANK_ACCOUNTS_SUCCESS':
      return {
        ...state,
        bankAccounts: action.payload,
        loading: { ...state.loading, bankAccounts: false },
      };
    case 'FETCH_BANK_ACCOUNTS_FAILURE':
      return {
        ...state,
        loading: { ...state.loading, bankAccounts: false },
        error: action.payload,
      };
    case 'ADD_BANK_ACCOUNT_START':
    case 'UPDATE_BANK_ACCOUNT_START':
      return {
        ...state,
        loading: { ...state.loading, bankAccounts: true },
      };
    case 'ADD_BANK_ACCOUNT_SUCCESS':
      return {
        ...state,
        bankAccounts: Array.isArray(action.payload) ? action.payload : [...(state.bankAccounts || []), action.payload],
        loading: { ...state.loading, bankAccounts: false },
      };
    case 'UPDATE_BANK_ACCOUNT_SUCCESS':
      return {
        ...state,
        bankAccounts: Array.isArray(action.payload) ? action.payload : (state.bankAccounts || []).map(account =>
          (account._id === action.payload._id || account.id === action.payload.id || account.id === action.payload._id) ? action.payload : account
        ),
        loading: { ...state.loading, bankAccounts: false },
      };
    case 'ADD_BANK_ACCOUNT_FAILURE':
    case 'UPDATE_BANK_ACCOUNT_FAILURE':
      return {
        ...state,
        loading: { ...state.loading, bankAccounts: false },
        error: action.payload,
      };

    // Transactions
    case 'FETCH_TRANSACTIONS_SUCCESS':
      return {
        ...state,
        transactions: action.payload,
      };

    // Symbols
    case 'FETCH_SYMBOLS_SUCCESS':
      return {
        ...state,
        symbols: action.payload,
      };

    // Popups
    case 'FETCH_POPUPS_SUCCESS':
      return {
        ...state,
        popups: action.payload,
      };

    default:
      return state;
  }
};

export default dataReducer;

